import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { SerializedSmartLink } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { SmartLinkStatsView } from '../smartLinkStatsView/SmartLinkStatsView';
import { SMLChartEmpty } from '../stats/SMLChartEmpty';
import { SmartLinkPublishBar } from './SmartLinkPublishBar';
import { isSmartLinkDraft } from '../utils/isSmartLinkDraft';
import { usePresaveCampaign } from '../presave/usePresaveCampaign';
import { usePublishPage } from '../presave/usePublishPage';
import { usePublishedPagesUsage } from '../usePublishedPagesUsage';
import { useMusic24Capabilities } from '../../music24/hooks/useMusic24Capabilities';
import { SmartLinkStatsDays } from '../../../redux/api/fymAPI.types';
import { SmartLinkStatsDaysPicker } from './SmartLinkStatsDaysPicker';

/** The first period: 14 days, or 7 when the plan history is shorter (Free). */
function firstStatsDays(historyDaysLimit: number): SmartLinkStatsDays {
  return historyDaysLimit < 14 ? 7 : 14;
}

interface SmartLinkManageStatsProps {
  smartLink: SerializedSmartLink;
}

/**
 * The analytics on the manage screen, told what page it is looking at.
 *
 * A pre-save page measures something the analytics pipeline cannot see on its
 * own: the campaign holds the subscriptions, and an unpublished page explains
 * its own empty chart. Both come from here rather than from the shared stats
 * components, so a plain SmartLink is untouched.
 *
 * The campaign query is the same one the panel above makes, so RTK Query serves
 * both from one request.
 *
 * The period picker locks the periods longer than the plan history (Free 7 days, Artist 90,
 * Pro and up 365). The server cuts the days to the plan too.
 */
const SmartLinkManageStatsComponent: React.FC<SmartLinkManageStatsProps> = ({ smartLink }) => {
  const intl = useIntl();
  const { historyDaysLimit } = useMusic24Capabilities();
  const [days, setDays] = useState<SmartLinkStatsDays>(() => firstStatsDays(historyDaysLimit));
  const { hasPresaveBlock, stats } = usePresaveCampaign({ accountId: smartLink.accountId, smartLink });
  const {
    publish,
    isLoading: isPublishing,
    error: publishError,
  } = usePublishPage({
    accountId: smartLink.accountId,
    smartLinkId: smartLink.id,
  });

  const presaveCount = useMemo(() => {
    if (!stats) {
      return undefined;
    }
    return stats.byService.reduce((sum, row) => sum + row.count, 0) + stats.emailCount;
  }, [stats]);

  const isDraft = isSmartLinkDraft(smartLink);

  const { limit: publishedPagesLimit, used: publishedPagesUsed } = usePublishedPagesUsage(smartLink.accountId);

  const publishAction = useMemo(() => {
    if (publishedPagesLimit !== null && publishedPagesUsed !== undefined) {
      return (
        <SmartLinkPublishBar
          used={publishedPagesUsed}
          limit={publishedPagesLimit}
          onPublish={publish}
          isPublishing={isPublishing}
          publishError={publishError}
        />
      );
    }
    return (
      <SMLButton size="sm" color="primary" onClick={publish} isLoading={isPublishing} error={publishError}>
        {intl.formatMessage({ id: 'app.sml.stats.chart.empty_draft.publish', defaultMessage: 'Publish page' })}
      </SMLButton>
    );
  }, [intl, isPublishing, publish, publishError, publishedPagesLimit, publishedPagesUsed]);

  const emptyChart = useMemo(() => {
    if (!hasPresaveBlock || !isDraft) {
      return undefined;
    }
    return (
      <SMLChartEmpty
        title={intl.formatMessage({
          id: 'app.sml.stats.chart.empty_draft.title',
          defaultMessage: "The page isn't published yet",
        })}
        text={intl.formatMessage({
          id: 'app.sml.stats.chart.empty_draft.text',
          defaultMessage:
            'Publish it and traffic starts landing here — views, taps per service, and where fans came from.',
        })}
      >
        {publishAction}
      </SMLChartEmpty>
    );
  }, [hasPresaveBlock, intl, isDraft, publishAction]);

  return (
    <>
      <div className="d-flex justify-content-end mb-2">
        <SmartLinkStatsDaysPicker days={days} historyDaysLimit={historyDaysLimit} onChange={setDays} />
      </div>
      <SmartLinkStatsView
        smartLink={smartLink}
        hasCampaign={hasPresaveBlock}
        presaveCount={presaveCount}
        emptyChart={emptyChart}
        days={days}
      />
    </>
  );
};

export const SmartLinkManageStats = TypedMemo(SmartLinkManageStatsComponent);
